import React, { useState } from 'react'
import Modal from '../modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'

// Cancel-booking control for a holiday package. `booking` is the package booking
// object shared by GET /users/trips/{id} and POST /packages/bookings/payment/verify/
// (booking_id, package_name, status, can_cancel, travel_date).
// The button is always visible: enabled only when `can_cancel === true`, otherwise
// disabled with a hint. Success/error toasts come from the service interceptor.
const PackageCancelButton = ({ booking, onCancelled, inline = false }) => {
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const canCancel = booking?.can_cancel === true

    // API only sends a boolean (`can_cancel`): already-cancelled and travel-date-passed
    // get their own hint, everything else points at the cancellation policy.
    const status = String(booking?.status || '').toLowerCase()
    const travelDate = booking?.travel_date ? new Date(`${booking.travel_date}T00:00:00`) : null
    const travelDatePassed = travelDate && !Number.isNaN(travelDate.getTime()) && travelDate < new Date()
    let disabledReason = 'Cancellation is not available for this booking as per the package cancellation policy.'
    if (status === 'cancelled' || status === 'refunded') {
        disabledReason = 'This booking has already been cancelled.'
    } else if (travelDatePassed) {
        disabledReason = 'Cancellation is not available because the travel date has already passed.'
    }

    // Swagger: POST /packages/bookings/cancel/ { booking_id: int, reason: string }.
    // No reason input in the UI yet, so a fixed user-cancel reason is sent.
    const handleCancel = async () => {
        if (!canCancel || loading) return
        setLoading(true)
        try {
            const { data } = await service.post(endpoints.packages.cancelBooking, {
                booking_id: Number(booking.booking_id),
                reason: 'Cancelled by customer',
            })
            if (onCancelled) onCancelled(data?.data)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setLoading(false)
            setConfirmOpen(false)
        }
    }

    const button = (
        <button
            type='button'
            className='bookingDownloadBtn tripDocCancelBtn'
            onClick={() => setConfirmOpen(true)}
            disabled={!canCancel || loading}
        >
            {loading ? 'Cancelling...' : 'Cancel Booking'}
        </button>
    )

    return (
        <>
            {/* inline: rendered inside the caller's own `.bookingConfirmActions` flex row
                (alongside other buttons) — the hint then wraps onto its own full-width line. */}
            {inline ? button : <div className='bookingConfirmActions mt-2'>{button}</div>}
            {!canCancel && <p className={`fare-extra-hint ${inline ? 'packageCancelHint' : 'mt-2'}`}>{disabledReason}</p>}

            <Modal open={confirmOpen} onClose={() => { if (!loading) setConfirmOpen(false) }} maxWidth={380}>
                <div className='logoutConfirm'>
                    <div className='logoutConfirm__title'>Cancel booking?</div>
                    <p className='logoutConfirm__desc'>
                        Are you sure you want to cancel {booking?.package_name || 'this package'} booking? This action cannot be undone, and any refund will follow the package cancellation policy.
                    </p>
                    <div className='logoutConfirm__actions'>
                        <button
                            type='button'
                            className='commonBtnTransparent'
                            onClick={() => setConfirmOpen(false)}
                            disabled={loading}
                        >
                            Keep Booking
                        </button>
                        <button
                            type='button'
                            className='commonBtn'
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            {loading ? 'Cancelling...' : 'Yes, Cancel'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default PackageCancelButton
